/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-param-reassign */
import React from 'react';
import { Link } from 'react-router-dom';
import Editor from '@monaco-editor/react';
import Button from '../../Components/Button';
import Input from '../../Components/Input';
import { BuilderActionType, BuilderContext } from './state';
import VendexAction from '../../Model/VendexAction';
import IconButton from '../../Components/IconButton';
import {
    ArrowDown,
    ArrowUp,
    Code,
    Link2,
    Plus,
    Trash2,
    Zap,
} from '../../Components/Icon';
import Select from '../../Components/Select';
import {
    BuilderMetaPill,
    BuilderPanel,
    BuilderPillButton,
} from './BuilderPanel';

type Props = {
    actions?: VendexAction[];
    onChange: (actions: VendexAction[]) => void;
    defaultTrigger?: string;
    showTriggerSelection?: boolean;
};

const defaultFunctions = [
    'CHANGE_PAGE',
    'SELECT_PRODUCT',
    'PRODUCT_VEND',
    'VEND_BY_TOKEN',
    'SET_DATA',
    'EMAIL_IS_VALID',
    'PHONE_IS_VALID',
    'ZIP_IS_VALID',
    'HAS_VALUE',
];

const payloadRequiredActions = ['SELECT_PRODUCT', 'SET_DATA', 'CHANGE_PAGE'];
const payloadlessActions = ['PRODUCT_VEND', 'VEND_BY_TOKEN'];
const customProductValue = '__CUSTOM_PRODUCT__';

type ActionProps = {
    action?: VendexAction;
    onChange: (value: VendexAction) => void;
    handleRemove?: () => void;
    handleMoveUp?: () => void;
    handleMoveDown?: () => void;
    canMoveUp?: boolean;
    canMoveDown?: boolean;
    isCustomNew?: boolean;
    onClose?: () => void;
    products: { name: string; id: string }[];
    defaultTrigger?: string;
    showTriggerSelection: boolean;
};

function Action({
    action,
    onChange,
    products,
    onClose,
    handleRemove,
    handleMoveUp,
    handleMoveDown,
    canMoveUp = false,
    canMoveDown = false,
    defaultTrigger,
    showTriggerSelection,
    isCustomNew = false,
}: ActionProps) {
    const [_action, setAction] = React.useState(action);
    const isNew = !action;
    const [isCustom, setIsCustom] = React.useState(isCustomNew);
    const [isCustomProduct, setIsCustomProduct] = React.useState(false);
    const { pages, script } = React.useContext(BuilderContext).state;
    const { dispatch } = React.useContext(BuilderContext);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const editorRef = React.useRef<any>(null);

    React.useEffect(() => {
        if (action) {
            const temp = defaultFunctions.includes(action.type);
            setIsCustom(!temp);
        }
    }, [action]);

    React.useEffect(() => {
        if (_action?.type !== 'SELECT_PRODUCT') {
            setIsCustomProduct(false);
            return;
        }

        const matchesKnownProduct = products.some(
            (product) => product.id === _action.payload,
        );
        setIsCustomProduct(Boolean(_action.payload) && !matchesKnownProduct);
    }, [_action?.payload, _action?.type, products]);

    const getValue = () => {
        let newString = 'payload => {\n\treturn true;\n }';
        if (_action?.type) {
            if (script && script[_action.type]) {
                newString = script[_action.type]?.toString() as string;
            }
        }
        return newString;
    };

    React.useEffect(() => {
        if (editorRef.current) {
            let newString = 'payload => {\n\treturn true;\n }';

            if (_action?.type && script && script[_action.type]) {
                newString = script[_action.type]?.toString() as string;
            }

            editorRef.current.setValue(newString);
        }
    }, [_action?.type, isCustom, script]);

    const isPayloadRequired = (value?: string) => {
        if (value) return payloadRequiredActions.includes(value);
        return false;
    };

    const isValid = () => {
        if (_action) {
            if (!_action.type) return false;
            if (isPayloadRequired(_action.type) && !_action.payload)
                return false;
            return true;
        }
        return false;
    };

    const handleTypeChange = (value: string) => {
        let payload = _action?.payload;
        if (value === 'SET_DATA') {
            payload = `PROPERTY, VALUE`;
        }
        if (payloadlessActions.includes(value)) {
            payload = undefined;
        }
        setAction({ ..._action, type: value, payload });
    };

    const handlePayload = (value: string) => {
        setAction({ ...(_action as VendexAction), payload: value });
    };

    const handleSelectProductPayload = (value: string) => {
        if (value === customProductValue) {
            setIsCustomProduct(true);
            setAction({
                ...(_action as VendexAction),
                payload: '',
            });
            return;
        }

        setIsCustomProduct(false);
        handlePayload(value);
    };

    const handleFunctionSave = () => {
        if (editorRef.current && _action?.type) {
            const val = editorRef.current.getValue() as string;
            const newScripts = script ? { ...script } : {};
            newScripts[_action?.type] = val.toString();
            dispatch({
                type: BuilderActionType.SET_SCRIPTS,
                payload: newScripts,
            });
        }
    };

    return (
        <div className="relative rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            {handleRemove && (
                <div className="absolute right-3 top-3 flex gap-3">
                    <IconButton
                        onClick={handleMoveUp}
                        btnSize="small"
                        disabled={!canMoveUp}
                        title="Move action up"
                        aria-label="Move action up"
                    >
                        <ArrowUp />
                    </IconButton>
                    <IconButton
                        onClick={handleMoveDown}
                        btnSize="small"
                        disabled={!canMoveDown}
                        title="Move action down"
                        aria-label="Move action down"
                    >
                        <ArrowDown />
                    </IconButton>
                    <IconButton
                        onClick={handleRemove}
                        className="text-red-600"
                        btnSize="small"
                        title="Remove action"
                        aria-label="Remove action"
                    >
                        <Trash2 />
                    </IconButton>
                </div>
            )}
            <div className="mb-4 flex flex-wrap items-center gap-2">
                <BuilderMetaPill>
                    {isNew ? 'New action' : _action?.type || 'Action'}
                </BuilderMetaPill>
                {!showTriggerSelection && (
                    <BuilderMetaPill className="bg-emerald-50 text-emerald-700">
                        Page event action
                    </BuilderMetaPill>
                )}
            </div>
            <div
                className={`grid gap-4 ${
                    !defaultTrigger && showTriggerSelection
                        ? 'md:grid-cols-2'
                        : 'md:grid-cols-1'
                }`}
            >
                <div>
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Type
                    </p>
                    {!isCustom ? (
                        <Select
                            value={_action?.type}
                            onChange={(e) => handleTypeChange(e.target.value)}
                            className="w-full px-2 py-3 rounded-sm box-border"
                        >
                            <option value="">SELECT ACTION</option>
                            {defaultFunctions.map((el) => (
                                <option key={el} value={el}>
                                    {el}
                                </option>
                            ))}
                        </Select>
                    ) : (
                        <Input
                            placeholder="Provide a name"
                            onChange={(e) => handleTypeChange(e.target.value)}
                            value={_action?.type ? _action.type : ''}
                        />
                    )}
                </div>
                {!defaultTrigger && showTriggerSelection && (
                    <div>
                        <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                            Trigger
                        </p>
                        <Select
                            value={_action?.trigger}
                            onChange={(e) =>
                                setAction({
                                    ...(_action as VendexAction),
                                    [e.target.name]: e.target.value,
                                })
                            }
                            className="w-full px-2 py-3 rounded-sm box-border"
                            name="trigger"
                        >
                            <option value="click">click</option>
                            <option value="focus">focus</option>
                            <option value="keypress">keypress</option>
                        </Select>
                    </div>
                )}
            </div>
            {!payloadlessActions.includes(_action?.type ?? '') && (
                <div className="mt-4">
                    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                        Payload
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                        Pass value to function if required
                    </p>
                    {_action?.type !== 'CHANGE_PAGE' &&
                        _action?.type !== 'SELECT_PRODUCT' && (
                            <Input
                                value={_action?.payload ? _action?.payload : ''}
                                onChange={(e) => handlePayload(e.target.value)}
                            />
                        )}
                    {_action?.type === 'SELECT_PRODUCT' && (
                        <>
                            <Select
                                value={
                                    isCustomProduct
                                        ? customProductValue
                                        : _action?.payload ?? ''
                                }
                                onChange={(e) =>
                                    handleSelectProductPayload(e.target.value)
                                }
                                className="w-full px-2 py-3 rounded-sm box-border"
                            >
                                <option value="">SELECT PRODUCT</option>
                                {products.map((el) => (
                                    <option key={el.id} value={el.id}>
                                        {el.name}
                                    </option>
                                ))}
                                <option value={customProductValue}>
                                    Custom product id
                                </option>
                            </Select>
                            {isCustomProduct && (
                                <Input
                                    value={_action?.payload ?? ''}
                                    onChange={(e) =>
                                        handlePayload(e.target.value)
                                    }
                                    placeholder="Enter custom product id"
                                    helperText="Use this when the product id should be typed manually instead of selected from the list."
                                />
                            )}
                        </>
                    )}
                    {_action?.type === 'CHANGE_PAGE' && (
                        <Select
                            value={_action?.payload ? _action?.payload : ''}
                            onChange={(e) => handlePayload(e.target.value)}
                            className="w-full px-2 py-3 rounded-sm box-border"
                        >
                            <option value="">SELECT PAGE</option>
                            {pages.map((el) => (
                                <option key={el.id} value={el.id}>
                                    {el.name}
                                </option>
                            ))}
                        </Select>
                    )}
                </div>
            )}
            {_action?.type === 'VEND_BY_TOKEN' && (
                <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <p className="font-semibold">Session data required</p>
                    <p className="mt-1">
                        This action reads from session data and does not use an
                        action payload.
                    </p>
                    <p className="mt-2">
                        Use <span className="font-semibold">SET_DATA</span>{' '}
                        before this action to provide either{' '}
                        <span className="font-semibold">token</span> or{' '}
                        <span className="font-semibold">id</span>.
                    </p>
                    <p className="mt-2">
                        <span className="font-semibold">token</span> is used by
                        default. If it is missing, the viewer uses{' '}
                        <span className="font-semibold">id</span> to create a
                        token in flight and continue vending.
                    </p>
                    <p className="mt-2">
                        `VEND_BY_TOKEN` prevents duplicate vends for the same id
                        or token.
                    </p>
                </div>
            )}
            {isCustom && (
                <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-2">
                        <div>
                            <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
                                Function
                            </p>
                            <p className="mt-1 text-xs text-slate-400">
                                Advanced custom behavior
                            </p>
                        </div>
                        <Link
                            to="/documentation"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-700"
                        >
                            <Link2 className="h-4 w-4" />
                            Read documentation
                        </Link>
                    </div>
                    <Editor
                        theme="vs-dark"
                        width="100%"
                        height="320px"
                        defaultLanguage="javascript"
                        defaultValue={getValue()}
                        // eslint-disable-next-line no-return-assign
                        onMount={(ref) => (editorRef.current = ref)}
                    />
                </div>
            )}
            <div className="mt-4 flex justify-end gap-2">
                <Button
                    onClick={() => {
                        setAction(action);
                        if (onClose) onClose();
                    }}
                    disabled={action === _action && !isNew}
                    className="bg-slate-100 text-slate-700 px-4 py-2 text-xs normal-case shadow-none"
                >
                    Cancel
                </Button>
                <Button
                    disabled={!isValid()}
                    onClick={() => {
                        onChange(_action as VendexAction);
                        handleFunctionSave();
                        if (onClose) onClose();
                    }}
                    className="bg-primary text-white px-4 py-2 text-xs normal-case shadow-sm"
                >
                    {isNew ? 'Add' : 'Update'}
                </Button>
            </div>
        </div>
    );
}

function ActionSelector({
    onChange,
    actions = [],
    defaultTrigger,
    showTriggerSelection = true,
}: Props) {
    const [newAction, setNewAction] = React.useState(0);

    const products = window.DATA?.products ?? [];

    const handleRemove = (index: number) => {
        const temp = [...actions];
        temp.splice(index, 1);
        onChange(temp);
    };

    const handleAdd = (value: VendexAction) => {
        if (defaultTrigger) {
            value.trigger = defaultTrigger as any;
        }
        const temp = [...actions];
        temp.push(value);
        onChange(temp);
    };

    const handleChange = (index: number, action: VendexAction) => {
        const temp = [...actions];
        temp[index] = action;
        onChange(temp);
    };

    const handleMove = (index: number, direction: -1 | 1) => {
        const nextIndex = index + direction;
        if (nextIndex < 0 || nextIndex >= actions.length) return;

        const temp = [...actions];
        [temp[index], temp[nextIndex]] = [temp[nextIndex], temp[index]];
        onChange(temp);
    };

    return (
        <BuilderPanel
            title="Actions"
            icon={<Zap className="h-4 w-4" />}
            className="mt-6"
            description="Add existing runtime actions or custom functions without leaving the current element."
            actions={
                newAction === 0 ? (
                    <>
                        <BuilderMetaPill>
                            {actions.length} action
                            {actions.length === 1 ? '' : 's'}
                        </BuilderMetaPill>
                        <BuilderPillButton
                            onClick={() => setNewAction(1)}
                            tone="accent"
                            icon={<Plus className="h-4 w-4" />}
                        >
                            Add action
                        </BuilderPillButton>
                        <BuilderPillButton
                            onClick={() => setNewAction(2)}
                            icon={<Code className="h-4 w-4" />}
                        >
                            Custom function
                        </BuilderPillButton>
                    </>
                ) : undefined
            }
            bodyClassName="space-y-4"
        >
            {actions.length === 0 && newAction === 0 && (
                <div className="rounded-2xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
                    No actions configured yet. Add a standard action or attach a
                    custom function for advanced behavior.
                </div>
            )}
            {actions.map((el, index) => (
                <Action
                    key={`${el.type}${index.toString()}`}
                    onChange={(value) => handleChange(index, value)}
                    action={el}
                    products={products}
                    defaultTrigger={defaultTrigger}
                    showTriggerSelection={showTriggerSelection}
                    handleRemove={() => handleRemove(index)}
                    handleMoveUp={() => handleMove(index, -1)}
                    handleMoveDown={() => handleMove(index, 1)}
                    canMoveUp={index > 0}
                    canMoveDown={index < actions.length - 1}
                />
            ))}
            {newAction !== 0 ? (
                <Action
                    onChange={(value) => handleAdd(value)}
                    products={products}
                    isCustomNew={newAction === 2}
                    onClose={() => setNewAction(0)}
                    defaultTrigger={defaultTrigger}
                    showTriggerSelection={showTriggerSelection}
                />
            ) : null}
        </BuilderPanel>
    );
}
export default ActionSelector;
